import type {
  FramingOpeningRange,
  FramingSpacingOriginPoint,
  OpeningLayoutBounds,
  PerimeterLayoutBounds,
} from "building-types/framing/framingLayoutTypes";
import type { BoundaryVertex, FramingBoundaryWithMetadata } from "building-types/framing/framingMetaDataTypes";
import { DIST_EPSILON } from "geom/core";
import { polyLine2Vertex, polyLine2VertexCount } from "geom/polyLine2";
import type { Vec2 } from "geom/vec2";
import { vec2Dot } from "geom/vec2";

const TWO_PI = 2 * Math.PI;

/**
 * Framing {@link FramingMetadata.direction} is an undirected member axis: θ and θ + π are equivalent.
 * Canonical range is [0, π) so spacing (perpendicular) and ray cast side stay consistent.
 */
export function canonicalFramingDirectionAngle(angleRadians: number): number {
  let angle = angleRadians % TWO_PI;
  if (angle < 0) {
    angle += TWO_PI;
  }
  if (angle >= Math.PI - DIST_EPSILON) {
    angle -= Math.PI;
  }
  return angle;
}

/** Unit vector along the member run (canonical undirected axis). */
export function framingDirectionVector(angle: number): Vec2 {
  const canonicalAngle = canonicalFramingDirectionAngle(angle);
  return [Math.cos(canonicalAngle), Math.sin(canonicalAngle)];
}

/** Unit vector along spacing / stud layout (perpendicular to member run). */
export function framingPerpendicularVector(angle: number): Vec2 {
  const canonicalAngle = canonicalFramingDirectionAngle(angle);
  return [-Math.sin(canonicalAngle), Math.cos(canonicalAngle)];
}

export function computeProjectedDistance(point: Vec2, planeOrigin: Vec2, direction: Vec2): number {
  const dx = point[0] - planeOrigin[0];
  const dy = point[1] - planeOrigin[1];
  return vec2Dot(dx, dy, direction[0], direction[1]);
}

export function collectBoundaryVertices(
  perimeter: FramingBoundaryWithMetadata,
  openings: readonly FramingBoundaryWithMetadata[],
  planeOrigin: Vec2,
  perpDirection: Vec2,
  framingDirection: Vec2,
): BoundaryVertex[] {
  const vertices: BoundaryVertex[] = [];

  const perimeterVertexCount = polyLine2VertexCount(perimeter.polyline) - 1;
  for (let i = 0; i < perimeterVertexCount; i++) {
    const [x, y] = polyLine2Vertex(perimeter.polyline, i);
    const perpDistance = computeProjectedDistance([x, y], planeOrigin, perpDirection);
    const parallelDistance = computeProjectedDistance([x, y], planeOrigin, framingDirection);

    vertices.push({
      x,
      y,
      perpDistance,
      parallelDistance,
      boundaryType: "perimeter",
      boundaryIndex: -1,
      vertexIndex: i,
    });
  }

  for (let openingIdx = 0; openingIdx < openings.length; openingIdx++) {
    const opening = openings[openingIdx];
    const openingVertexCount = polyLine2VertexCount(opening.polyline) - 1;

    for (let i = 0; i < openingVertexCount; i++) {
      const [x, y] = polyLine2Vertex(opening.polyline, i);
      const perpDistance = computeProjectedDistance([x, y], planeOrigin, perpDirection);
      const parallelDistance = computeProjectedDistance([x, y], planeOrigin, framingDirection);

      vertices.push({
        x,
        y,
        perpDistance,
        parallelDistance,
        boundaryType: "opening",
        boundaryIndex: openingIdx,
        vertexIndex: i,
      });
    }
  }

  vertices.sort((a, b) => a.perpDistance - b.perpDistance);

  return vertices;
}

export function computeOpeningRanges(
  sortedVertices: BoundaryVertex[],
  numOpenings: number,
): { perpOpeningRanges: FramingOpeningRange[]; parallelOpeningRanges: FramingOpeningRange[] } {
  const perpOpeningRanges: FramingOpeningRange[] = [];
  const parallelOpeningRanges: FramingOpeningRange[] = [];

  for (let i = 0; i < numOpenings; i++) {
    const openingVertices = sortedVertices.filter((v) => v.boundaryType === "opening" && v.boundaryIndex === i);

    if (openingVertices.length === 0) {
      continue;
    }

    const minPerpDist = Math.min(...openingVertices.map((v) => v.perpDistance));
    const maxPerpDist = Math.max(...openingVertices.map((v) => v.perpDistance));

    perpOpeningRanges.push({
      openingIndex: i,
      minDist: minPerpDist,
      maxDist: maxPerpDist,
    });

    parallelOpeningRanges.push({
      openingIndex: i,
      minDist: Math.min(...openingVertices.map((v) => v.parallelDistance)),
      maxDist: Math.max(...openingVertices.map((v) => v.parallelDistance)),
    });
  }

  return { perpOpeningRanges, parallelOpeningRanges };
}

export function deriveOpeningLayoutBounds(
  openingIndex: number,
  perpRange: FramingOpeningRange,
  parallelRange: FramingOpeningRange,
): OpeningLayoutBounds {
  return {
    openingIndex,
    startPerp: perpRange.minDist,
    endPerp: perpRange.maxDist,
    bottomParallel: parallelRange.minDist,
    topParallel: parallelRange.maxDist,
  };
}

export function expandOpeningLayoutBoundsByBorder(
  bounds: OpeningLayoutBounds,
  roughOpeningBorder: number,
): OpeningLayoutBounds {
  if (roughOpeningBorder <= 0) {
    return bounds;
  }
  return {
    openingIndex: bounds.openingIndex,
    startPerp: bounds.startPerp - roughOpeningBorder,
    endPerp: bounds.endPerp + roughOpeningBorder,
    bottomParallel: bounds.bottomParallel - roughOpeningBorder,
    topParallel: bounds.topParallel + roughOpeningBorder,
  };
}

export function derivePerimeterLayoutBounds(sortedVertices: BoundaryVertex[]): PerimeterLayoutBounds | undefined {
  const perimeterVertices = sortedVertices.filter((v) => v.boundaryType === "perimeter");
  if (perimeterVertices.length === 0) {
    return undefined;
  }

  return {
    startPerp: Math.min(...perimeterVertices.map((v) => v.perpDistance)),
    endPerp: Math.max(...perimeterVertices.map((v) => v.perpDistance)),
    bottomParallel: Math.min(...perimeterVertices.map((v) => v.parallelDistance)),
    topParallel: Math.max(...perimeterVertices.map((v) => v.parallelDistance)),
  };
}

/**
 * Direction of the spacing-index grid from {@link generateFramingSpacingOriginPoints} toward the
 * perimeter (same rule as the spacing loop in that function).
 *
 * **Wall framing (V2) only.** Spacing runs from `startPoint` toward either min or max perp — not
 * both. That matches stud layout from a perimeter corner (first stud one spacing from layout start).
 * A mid-span `framingStartPt` only gets OC on one side of start (end studs still come from
 * `includeEnds`). Do not use for floor joists; see {@link generateJoistRayOriginPoints}.
 */
export function framingSpacingSign(sortedVertices: BoundaryVertex[], startPoint: Vec2, perpDirection: Vec2): 1 | -1 {
  if (sortedVertices.length === 0) {
    return 1;
  }
  const startPerpDist = computeProjectedDistance(startPoint, startPoint, perpDirection);
  const minPerpDist = sortedVertices[0].perpDistance;
  const maxPerpDist = sortedVertices[sortedVertices.length - 1].perpDistance;
  const distToMaxBoundary = maxPerpDist - startPerpDist;
  const distToMinBoundary = startPerpDist - minPerpDist;
  if (distToMinBoundary > distToMaxBoundary + DIST_EPSILON) {
    return -1;
  }
  return 1;
}

/**
 * One-sided spacing grid along the perpendicular axis (no opening-specific member typing).
 * Used by wall framing V2 ({@link WallFramingGenerator}). Floor joist spacing must cover the full
 * bay min→max and lives in {@link generateJoistRayOriginPoints} instead.
 */
export function generateFramingSpacingOriginPoints(
  sortedVertices: BoundaryVertex[],
  spacing: number,
  startPoint: Vec2,
  framingDirection: Vec2,
  perpDirection: Vec2,
  includeStart: boolean = true,
  includeEnds: boolean = true,
): FramingSpacingOriginPoint[] {
  if (sortedVertices.length === 0) {
    return [];
  }

  const RAY_ORIGIN_FRAMING_OFFSET = 1e6;
  const originPoints: FramingSpacingOriginPoint[] = [];

  const minPerpDist = sortedVertices[0].perpDistance;
  const maxPerpDist = sortedVertices[sortedVertices.length - 1].perpDistance;

  const startPerpDist = computeProjectedDistance(startPoint, startPoint, perpDirection);

  const baseX = startPoint[0] - RAY_ORIGIN_FRAMING_OFFSET * framingDirection[0];
  const baseY = startPoint[1] - RAY_ORIGIN_FRAMING_OFFSET * framingDirection[1];

  const spacingSign = framingSpacingSign(sortedVertices, startPoint, perpDirection);

  if (includeStart && startPerpDist >= minPerpDist - DIST_EPSILON && startPerpDist <= maxPerpDist + DIST_EPSILON) {
    const x = baseX + startPerpDist * perpDirection[0];
    const y = baseY + startPerpDist * perpDirection[1];
    originPoints.push({ x, y, perpDistance: startPerpDist });
  }

  if (includeEnds) {
    const minX = baseX + minPerpDist * perpDirection[0];
    const minY = baseY + minPerpDist * perpDirection[1];
    const isDuplicateMin = originPoints.some(
      (p) => Math.abs(p.x - minX) < DIST_EPSILON && Math.abs(p.y - minY) < DIST_EPSILON,
    );
    if (!isDuplicateMin) {
      originPoints.push({ x: minX, y: minY, perpDistance: minPerpDist });
    }

    const maxX = baseX + maxPerpDist * perpDirection[0];
    const maxY = baseY + maxPerpDist * perpDirection[1];
    const isDuplicateMax = originPoints.some(
      (p) => Math.abs(p.x - maxX) < DIST_EPSILON && Math.abs(p.y - maxY) < DIST_EPSILON,
    );
    if (!isDuplicateMax) {
      originPoints.push({ x: maxX, y: maxY, perpDistance: maxPerpDist });
    }
  }

  let spacingIndex = 1;
  for (;;) {
    const currentPerpDist = startPerpDist + spacingSign * spacingIndex * spacing;
    spacingIndex += 1;

    if (spacingSign > 0 && currentPerpDist > maxPerpDist + DIST_EPSILON) {
      break;
    }
    if (spacingSign < 0 && currentPerpDist < minPerpDist - DIST_EPSILON) {
      break;
    }

    const isDuplicate = originPoints.some((p) => Math.abs(p.perpDistance - currentPerpDist) < DIST_EPSILON);

    if (!isDuplicate) {
      const x = baseX + currentPerpDist * perpDirection[0];
      const y = baseY + currentPerpDist * perpDirection[1];
      originPoints.push({ x, y, perpDistance: currentPerpDist });
    }
  }

  originPoints.sort((a, b) => a.perpDistance - b.perpDistance);
  return originPoints;
}

/**
 * Rail perp distance for a spacing-grid centerline target. Lumber profiles extrude from the rail
 * corner, so the rail sits half a stud run thickness back from the spacing centerline.
 */
export function spacingOriginRailPerpDistance(
  centerlinePerpDistance: number,
  spacingSign: 1 | -1,
  studHalfThickness: number,
): number {
  return centerlinePerpDistance - spacingSign * studHalfThickness;
}

export function layoutPointFromPerpParallel(
  perpDistance: number,
  parallelDistance: number,
  planeOrigin: Vec2,
  perpDirection: Vec2,
  framingDirection: Vec2,
): Vec2 {
  return [
    planeOrigin[0] + perpDistance * perpDirection[0] + parallelDistance * framingDirection[0],
    planeOrigin[1] + perpDistance * perpDirection[1] + parallelDistance * framingDirection[1],
  ];
}
